import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

// Meeting food (møtemat) as a billing source.
//
// A customer who orders møtemat in the portal is buying food directly, which is
// a different charge from the subsidy their employees consume at the kiosk — so
// it becomes its own invoice line kind rather than being folded into either.
//
// ONLY approved portal orders are billable:
//   * `source: 'portal'` — an operator-created extra delivery is Frigg's own
//     (sales meeting, promotion, kick-off) and carries no kundekort to bill.
//   * `status: 'Sheduled'` — a pending, rejected or cancelled order was never
//     delivered. Approval always precedes delivery, so every order delivered in
//     a month was already approved within it; billing by delivery date cannot
//     strand a late approval.
//
// Amounts are the order's own snapshot, taken when the customer placed it, so a
// later product price change never rewrites an invoice. They are VAT-INCLUSIVE
// (the portal quotes the kiosk price the customer saw) — exactly like
// `creditConsumed`, and the billing page backs the customer's own VAT rate out
// of them the same way.

export interface MotematOrder {
  id: string;
  deliveryNumber: number;
  dateUnix: number;
  dateLabel: string;
  systemName: string;
  desciption: string;
  itemCount: number;
  grossIncVat: number;
}

export interface MotematBillable {
  grossIncVat: number;
  orders: MotematOrder[];
}

@Injectable({ providedIn: 'root' })
export class MotematBillingService {

  constructor(private afs: AngularFirestore) { }

  // Approved portal møtemat for a month, grouped by kundekort.
  //
  // Queried on the dateUnix RANGE only, with source/status filtered in memory:
  // adding either as an equality filter alongside the range would need a
  // composite index, and this project's indexes are created by hand in the
  // console. A month holds a handful of orders per company.
  async getBillableByCustomer(month: string, companyIds: string[]): Promise<{ [kundekortId: string]: MotematBillable }> {
    const from = moment(month, 'YYYY-MM').startOf('month').unix();
    const to = moment(month, 'YYYY-MM').endOf('month').unix();

    const out: { [kundekortId: string]: MotematBillable } = {};

    for (const companyId of companyIds.filter(Boolean)) {
      const snap = await this.afs
        .collection('stocking').doc(companyId).collection('extra-delivery',
          ref => ref.where('dateUnix', '>=', from).where('dateUnix', '<=', to))
        .ref.get();

      snap.docs.forEach(d => {
        const o = d.data() as any;
        if (o.source !== 'portal') { return; }
        if (o.status !== 'Sheduled') { return; }
        if (!o.customerId) { return; }          // nowhere to bill it

        const gross = Number(o.totalIncVat) || 0;
        if (!(gross > 0)) { return; }

        const bucket = out[o.customerId] || (out[o.customerId] = { grossIncVat: 0, orders: [] });
        bucket.grossIncVat += gross;
        bucket.orders.push({
          id: d.id,
          deliveryNumber: o.deliveryNumber,
          dateUnix: o.dateUnix,
          dateLabel: o.dateUnix ? moment.unix(o.dateUnix).format('DD.MM.YYYY') : '',
          systemName: o.systemName || '',
          desciption: o.desciption || '',
          itemCount: (o.products || []).reduce((n: number, p: any) => n + (Number(p.amount) || 0), 0),
          grossIncVat: +gross.toFixed(2)
        });
      });
    }

    Object.values(out).forEach(b => {
      b.grossIncVat = +b.grossIncVat.toFixed(2);
      b.orders.sort((a, z) => (a.dateUnix || 0) - (z.dateUnix || 0));
    });
    return out;
  }
}
